import type { PayloadAction } from '@reduxjs/toolkit';
import type { OpenJobsState, UpdateEscalationPayload } from 'src/types/open-jobs';
import { initialState } from 'src/redux/open-jobs/constants';

interface UpdateOpenJobPayload {
  jobId: number;
  data: Record<string, unknown>;
}

interface UpdateHasPendingActionPayload {
  srId: string;
}

interface OpenJobsPusherPayload {
  action: 'update' | 'remove' | 'new';
  job_id: number;
  status_group?: string;
  previous_status_group?: string;
  [key: string]: unknown;
}

interface UpdateOpenJobsPayload {
  data: {
    job_ids: number[];
    total_unread_chat_count: number;
  };
}

const reducers = {
  updateOpenJob: (state: OpenJobsState, action: PayloadAction<UpdateOpenJobPayload>) => {
    const { jobId, data } = action.payload;

    state.source = {
      ...state.source,
      [jobId]: { ...state.source[jobId], ...data },
    };
  },
  updateHasPendingActionInServiceRequest: (
    state: OpenJobsState,
    action: PayloadAction<UpdateHasPendingActionPayload>,
  ) => {
    const { srId } = action.payload;
    state.source = Object.keys(state.source).reduce((acc: Record<number, Record<string, unknown>>, jobId: string) => {
      const job = state.source[Number(jobId)] as Record<string, unknown>;
      if (job.service_request_id === srId && job.has_pending_action_in_service_request) {
        acc[Number(jobId)] = {
          ...job,
          has_pending_action_in_service_request: false,
        };
      } else {
        acc[Number(jobId)] = job;
      }
      return acc;
    }, {}) as OpenJobsState['source'];
  },
  resetOpenJobs: () => initialState,

  updateOpenJobsOnPusher: (
    state: OpenJobsState,
    action: PayloadAction<OpenJobsPusherPayload>,
  ) => {
    const { payload } = action;
    const removeFromPreviousStatusGroup = () => {
      state.data = {
        ...(state?.data || {}),
        [payload?.previous_status_group as string]: {
          ...(state?.data?.[payload?.previous_status_group as string] || {}),
          data: (state?.data?.[payload?.previous_status_group as string]?.data || [])
            .filter((id) => id !== payload?.job_id),
        },
      } as OpenJobsState['data'];
    };
    if (action?.payload?.action === 'update') {
      // remove job from previous status group and add to new status group
      if (payload?.previous_status_group) {
        removeFromPreviousStatusGroup();
        state.data = {
          ...(state?.data || {}),
          [payload?.status_group as string]: {
            ...(state?.data?.[payload?.status_group as string] || {}),
            data: Array.from(new Set([...(state?.data?.[payload?.status_group as string]?.data || []),
              payload?.job_id])),
          },
        } as OpenJobsState['data'];
        state.source = {
          ...(state.source || {}),
          [payload?.job_id]: { ...(payload || {}) },
        } as OpenJobsState['source'];
      }
      if (payload?.status_group === (state?.source?.[payload?.job_id] as Record<string, unknown>)?.status_group) {
        state.source = {
          ...(state.source || {}),
          [payload?.job_id]: { ...(payload || {}) },
        } as OpenJobsState['source'];
      }
    }
    if (action.payload.action === 'remove') {
      state.source = Object.keys(state.source).reduce((acc: Record<number, Record<string, unknown>>, jobId: string) => {
        if (Number(jobId) !== Number(payload?.job_id)) {
          acc[Number(jobId)] = state.source[Number(jobId)] as Record<string, unknown>;
        }
        return acc;
      }, {}) as OpenJobsState['source'];
      if (payload?.previous_status_group) {
        removeFromPreviousStatusGroup();
      }
    }
    if (action.payload.action === 'new') {
      if (!state.source?.[payload?.job_id]) {
        state.source = {
          ...(state.source || {}),
          [payload?.job_id]: { ...(payload || {}) },
        } as OpenJobsState['source'];
      }
      if (!state?.data?.[payload?.status_group as string]?.data?.some((item) => item === payload?.job_id)) {
        state.data = {
          ...(state.data || {}),
          [payload?.status_group as string]: {
            ...(state.data?.[payload?.status_group as string] || {}),
            data: Array.from(new Set([...(state?.data?.[payload?.status_group as string]?.data || []),
              payload?.job_id])),
          },
        } as OpenJobsState['data'];
      }
    }
  },

  updateOpenJobs: (state: OpenJobsState, action: PayloadAction<UpdateOpenJobsPayload>) => {
    const { data } = action.payload;
    data?.job_ids?.forEach((item) => {
      if (state.source?.[item]) {
        state.source = {
          ...(state.source || {}),
          [item]: {
            ...(state.source[item] || {}),
            unread_chat_count: data?.total_unread_chat_count,
          },
        } as OpenJobsState['source'];
      }
    });
  },

  updateEscalationOnPusher: (
    state: OpenJobsState,
    action: PayloadAction<UpdateEscalationPayload>,
  ) => {
    const { job_ids, type, reason } = action.payload;
    const isEscalated = type === 'escalation';
    job_ids?.forEach((id) => {
      if (state.source?.[id]) {
        state.source = {
          ...(state.source || {}),
          [id]: {
            ...(state.source[id] || {}),
            is_escalated: isEscalated,
            escalated_tooltip: isEscalated ? reason : null,
          },
        } as OpenJobsState['source'];
      }
    });
  },
};

export default reducers;
